/**
 * Secret Entity — Jotai Atoms
 *
 * Pattern: minimal molecule — query + mutation only. No draft semantics,
 * no isDirty, no imperative get/set scaffolding. Vault is not an artifact,
 * it has no revision lineage; the molecule shape would only contribute
 * dead methods.
 *
 * Important invariants preserved from OSS:
 *
 *   1. Query key MUST stay byte-identical with the OSS legacy path so
 *      that any transitional state (e.g. multiple consumers being
 *      swept) hits one cache entry, not two:
 *
 *          ["vault", "secrets", user?.id, projectId]
 *
 *   2. The migration atom (`vaultMigrationAtom` + `migrateVaultKeysAtom`)
 *      runs at most once per authenticated session. The setter
 *      early-returns if `migrating || migrated` so it is idempotent at
 *      the action level. The `useVaultSecret` hook gates the trigger
 *      with `user && !migrating && !migrated`, and resets to
 *      `{migrating: false, migrated: false}` on logout so a subsequent
 *      sign-in in the same session can re-run the migration.
 *
 *   3. Project scoping comes from `@agenta/shared/state.projectIdAtom`,
 *      which OSS hydrates from the URL via the existing
 *      `setProjectIdAtom` wiring. User identity comes from
 *      `@agenta/shared/state.userAtom`, hydrated by OSS `UserListener`.
 */

import {projectIdAtom, userAtom} from "@agenta/shared/state"
import type {LlmProvider} from "@agenta/shared/types"
import {
    llmAvailableProviders,
    llmAvailableProvidersToken,
    removeEmptyFromObjects,
} from "@agenta/shared/utils"
import type {QueryKey} from "@tanstack/react-query"
import {atom} from "jotai"
import {atomWithStorage} from "jotai/utils"
import {atomWithMutation, atomWithQuery} from "jotai-tanstack-query"
import {z} from "zod"

import {safeParseWithLogging} from "../../shared/utils/zodSchema"
import {createVaultSecret, deleteVaultSecret, fetchVaultSecret, updateVaultSecret} from "../api/api"
import {
    getEnvNameMap,
    transformCustomProviderPayloadData,
    transformCustomSecretPayloadData,
    transformStandardProviderPayloadData,
    toUpdateSecretPayload,
} from "../core/transforms"
import {
    SecretKind,
    type CreateSecretDto,
    type NamedSecretRow,
    type VaultMigrationStatus,
} from "../core/types"

import {vaultSecretsPersister} from "./persistence"

interface CreateMutationArgs {
    projectId: string
    payload: CreateSecretDto
}
interface UpdateMutationArgs {
    projectId: string
    secret_id: string
    payload: Parameters<typeof updateVaultSecret>[0]["payload"]
}
interface DeleteMutationArgs {
    projectId: string
    secret_id: string
}

/**
 * Atom for tracking vault key migration status.
 * Used to ensure migration only happens once and track its progress.
 */
export const vaultMigrationAtom = atom<VaultMigrationStatus>({
    migrating: false,
    migrated: false,
})

/** Persisted "user has connected a provider key at least once" flag, gating the connect-model prompt. */
export const providerKeySetupDoneAtom = atomWithStorage<boolean>(
    "agenta:provider-key-setup-done",
    false,
    undefined,
    {getOnInit: true},
)

/**
 * Query atom for fetching vault secrets.
 * Only enabled when user is authenticated and a project is selected.
 *
 * The query key includes `user?.id` so that switching users invalidates
 * the cache (a different user's secrets must not leak through React Query's
 * cache).
 *
 * Persistence (Class C): paints from a REDACTED IndexedDB projection (secret
 * values replaced with a truthy sentinel — see `./persistence`) and ALWAYS
 * fires one background refetch on restore, so real values only ever live in
 * memory.
 */
export const vaultSecretsQueryAtom = atomWithQuery<LlmProvider[]>((get) => {
    const user = get(userAtom)
    // Read migration status to keep this atom subscribed to migration changes
    // (matches OSS behavior — migration completion can trigger a refetch).
    const _migrationStatus = get(vaultMigrationAtom)
    const projectId = get(projectIdAtom)

    return {
        queryKey: ["vault", "secrets", user?.id, projectId],
        queryFn: async () => {
            if (!projectId) {
                throw new Error("[vault] Missing projectId for fetchVaultSecret")
            }

            return await fetchVaultSecret({projectId})
        },
        staleTime: 1000 * 60 * 5, // 5 minutes
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: true,
        enabled: !!user && !!projectId,
        persister: vaultSecretsPersister.persisterFn<LlmProvider[], QueryKey>,
    }
})

/**
 * Derived atom for standard provider secrets.
 * Maps the canonical provider catalog to vault data, attaching the stored
 * `key` / `id` / `created_at` for any matched provider.
 */
export const standardSecretsAtom = atom((get) => {
    const queryResult = get(vaultSecretsQueryAtom)
    const data = queryResult.data || []

    return llmAvailableProviders.map((secret) => {
        const match = data.find((item: LlmProvider) => item.name === secret.name)
        if (match) {
            return {
                ...secret,
                key: match.key,
                // A write-only record answers "is it configured" here; its value never arrives.
                writeOnly: match.writeOnly,
                hasKey: match.hasKey,
                keyPreview: match.keyPreview,
                managementPolicy: match.managementPolicy,
                id: match.id,
                // The connection's saved policy round-trips: a form seeded from this row
                // sends it back on update instead of dropping it.
                models: match.models,
                harnesses: match.harnesses,
                created_at: match.created_at,
            }
        } else {
            return secret
        }
    })
})

/**
 * Derived atom for custom provider secrets.
 * Filters vault data for custom provider configurations.
 */
export const customSecretsAtom = atom((get) => {
    const queryResult = get(vaultSecretsQueryAtom)
    const data = queryResult.data || []

    return data.filter((secret) => secret.type === SecretKind.CustomProvider)
})

/**
 * Derived atom for user-named secrets (`custom_secret`).
 * Filters vault data for named-secret rows (text/json blobs).
 */
export const customNamedSecretsAtom = atom((get) => {
    const queryResult = get(vaultSecretsQueryAtom)
    const data = queryResult.data || []

    return data.filter((secret) => secret.type === SecretKind.CustomSecret) as NamedSecretRow[]
})

/**
 * Mutation atom for creating vault secrets.
 *
 * Callers pass `projectId` explicitly via `mutateAsync`. Reading it from
 * the calling atom's `get` (and forwarding it here) keeps this atom free
 * of `getDefaultStore()`/global-store assumptions.
 */
export const createVaultSecretMutationAtom = atomWithMutation(() => ({
    mutationFn: ({projectId, payload}: CreateMutationArgs) =>
        createVaultSecret({projectId, payload}),
}))

/**
 * Mutation atom for updating vault secrets.
 */
export const updateVaultSecretMutationAtom = atomWithMutation(() => ({
    mutationFn: ({projectId, secret_id, payload}: UpdateMutationArgs) =>
        updateVaultSecret({projectId, secret_id, payload}),
}))

/**
 * Mutation atom for deleting vault secrets.
 */
export const deleteVaultSecretMutationAtom = atomWithMutation(() => ({
    mutationFn: ({projectId, secret_id}: DeleteMutationArgs) =>
        deleteVaultSecret({projectId, secret_id}),
}))

/**
 * Atom for creating standard provider vault secrets.
 * Handles the payload creation and provider mapping.
 */
export const createStandardSecretAtom = atom(null, async (get, set, provider: LlmProvider) => {
    const envNameMap = getEnvNameMap()
    const standardSecrets = get(standardSecretsAtom)
    const createMutation = get(createVaultSecretMutationAtom)
    const updateMutation = get(updateVaultSecretMutationAtom)
    const projectId = get(projectIdAtom)
    if (!projectId) {
        throw new Error("[vault] Missing projectId for createStandardSecret")
    }

    try {
        const providerKind = envNameMap[provider.name as string]
        if (!providerKind) {
            throw new Error(
                `[vault] Unknown provider name "${provider.name}" when creating standard secret`,
            )
        }
        if (!provider.key) {
            throw new Error(
                `[vault] Missing key for provider "${provider.name}" when creating standard secret`,
            )
        }

        const payload = transformStandardProviderPayloadData(provider, providerKind)

        const findSecret = standardSecrets.find((s) => s.name === provider.name)
        // Prefer the id from the matched server-side record so we still hit
        // the update path when the caller passes a `LlmProvider` that's
        // missing `id` (e.g. form state seeded from the canonical catalog
        // before the vault query resolved).
        const secretId = findSecret?.id ?? provider.id

        if (secretId) {
            await updateMutation.mutateAsync({projectId, secret_id: secretId, payload})
        } else {
            await createMutation.mutateAsync({projectId, payload})
        }
    } catch (error) {
        console.error("Failed to create/update standard secret:", error)
        throw error
    }
})

/**
 * Atom for creating custom provider vault secrets.
 * Handles custom provider payload transformation and cleanup.
 */
export const createCustomSecretAtom = atom(null, async (get, set, provider: LlmProvider) => {
    const customSecrets = get(customSecretsAtom)
    const createMutation = get(createVaultSecretMutationAtom)
    const updateMutation = get(updateVaultSecretMutationAtom)
    const projectId = get(projectIdAtom)
    if (!projectId) {
        throw new Error("[vault] Missing projectId for createCustomSecret")
    }

    try {
        const rawPayload = transformCustomProviderPayloadData(provider)
        const payload = removeEmptyFromObjects(rawPayload) as CreateSecretDto

        const findSecret = customSecrets.find((s) => s.id === provider.id)
        const secretId = findSecret?.id ?? provider.id

        if (secretId) {
            await updateMutation.mutateAsync({projectId, secret_id: secretId, payload})
        } else {
            await createMutation.mutateAsync({projectId, payload})
        }
    } catch (error) {
        console.error("Failed to create/update custom secret:", error)
        throw error
    }
})

/**
 * Atom for creating/updating user-named secrets (`custom_secret`).
 *
 * Unlike the provider atoms, the payload is NOT run through
 * `removeEmptyFromObjects`: a `text` secret may legitimately be an empty
 * string and a `json` secret may carry `null` values — both must survive to
 * the backend verbatim.
 */
export const createCustomNamedSecretAtom = atom(null, async (get, set, secret: NamedSecretRow) => {
    const namedSecrets = get(customNamedSecretsAtom)
    const createMutation = get(createVaultSecretMutationAtom)
    const updateMutation = get(updateVaultSecretMutationAtom)
    const projectId = get(projectIdAtom)
    if (!projectId) {
        throw new Error("[vault] Missing projectId for createCustomNamedSecret")
    }

    try {
        const payload = transformCustomSecretPayloadData(secret)

        const findSecret = namedSecrets.find((s) => s.id === secret.id)
        const secretId = findSecret?.id ?? secret.id

        if (secretId) {
            await updateMutation.mutateAsync({
                projectId,
                secret_id: secretId,
                payload: toUpdateSecretPayload(payload),
            })
        } else {
            await createMutation.mutateAsync({projectId, payload})
        }
    } catch (error) {
        console.error("Failed to create/update named secret:", error)
        throw error
    }
})

/**
 * Atom for deleting vault secrets.
 */
export const deleteSecretAtom = atom(null, async (get, set, provider: LlmProvider) => {
    const deleteMutation = get(deleteVaultSecretMutationAtom)
    const projectId = get(projectIdAtom)
    if (!projectId) {
        throw new Error("[vault] Missing projectId for deleteSecret")
    }

    try {
        if (provider.id) {
            await deleteMutation.mutateAsync({projectId, secret_id: provider.id})
        }
    } catch (error) {
        console.error("Failed to delete secret:", error)
        throw error
    }
})

/**
 * Migration atom for handling localStorage → vault migration.
 *
 * Idempotent at the action level: early-returns if already migrating or
 * migrated. The hook's `useEffect` is responsible for the user-presence
 * trigger and the logout reset (re-arm).
 *
 * Always ends in `{migrating: false, migrated: true}`, on failure too: a legacy payload
 * that cannot be parsed stays backed up in localStorage, and the vault UI must never wait
 * on it. The server list is the source of truth.
 */
export const migrateVaultKeysAtom = atom(null, async (get, set) => {
    const migrationStatus = get(vaultMigrationAtom)

    if (migrationStatus.migrating || migrationStatus.migrated) {
        return
    }

    set(vaultMigrationAtom, {migrating: true, migrated: false})

    try {
        const localStorageProviders = localStorage.getItem(llmAvailableProvidersToken)

        if (localStorageProviders) {
            const providers = parseLegacyProviders(localStorageProviders)
            const failed: LlmProvider[] = []
            if (providers) {
                for (const provider of providers) {
                    if (!provider.key) continue
                    try {
                        await set(createStandardSecretAtom, provider)
                    } catch (error) {
                        // One bad entry must not stop the others; it stays for the next load.
                        console.error("[vault] Legacy provider key was not migrated:", error)
                        failed.push(provider)
                    }
                }
            } else {
                console.error(
                    "[vault] Legacy provider keys could not be parsed; leaving them backed up.",
                )
            }

            // Keep a backup, then leave only the entries that still need a retry in place, in
            // the canonical double-encoded form. A later page load picks them up again.
            localStorage.setItem(`${llmAvailableProvidersToken}Backup`, localStorageProviders)
            if (failed.length > 0) {
                localStorage.setItem(
                    llmAvailableProvidersToken,
                    JSON.stringify(JSON.stringify(failed)),
                )
            } else {
                localStorage.removeItem(llmAvailableProvidersToken)
            }
        }
    } catch (error) {
        // A failed migration must not hold the vault UI in its loading state: the keys stay
        // in localStorage for a later attempt, and the app keeps working with the server list.
        console.error("Migration failed:", error)
    } finally {
        set(vaultMigrationAtom, {migrating: false, migrated: true})
    }
})

/** One legacy entry: whatever else it carried, only an object with a string key can migrate. */
const legacyProviderSchema = z.object({key: z.string().nullish()}).passthrough()

/**
 * The legacy localStorage payload was double-encoded JSON (a JSON string holding JSON), but
 * older builds wrote it once. Accept both; return null when the payload is not a list. Each
 * entry is validated on its own, so one malformed entry does not block the valid ones.
 */
function parseLegacyProviders(raw: string): LlmProvider[] | null {
    let parsed: unknown
    try {
        parsed = JSON.parse(raw)
        if (typeof parsed === "string") parsed = JSON.parse(parsed)
    } catch {
        return null
    }
    if (!Array.isArray(parsed)) return null
    const providers: LlmProvider[] = []
    for (const entry of parsed) {
        const valid = safeParseWithLogging(legacyProviderSchema, entry, "[vault] legacy provider")
        if (valid) providers.push(valid as unknown as LlmProvider)
    }
    return providers
}
